import React from 'react';
import { Box, CircularProgress, IconButton, Stack, Tab, Tabs, Typography } from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded';
import BookmarkRoundedIcon from '@mui/icons-material/BookmarkRounded';
import ForumRoundedIcon from '@mui/icons-material/ForumRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import SupportAgentRoundedIcon from '@mui/icons-material/SupportAgentRounded';
import TagRoundedIcon from '@mui/icons-material/TagRounded';
import { useTranslation } from 'react-i18next';
import type { ChatParticipant } from '../../models/Chat';
import type { ChatAvatarMap } from '../../utils/chatAvatars';
import { chatPageSx } from './chatLayout';
import { ChatMemberAvatars, ChatMemberChips } from './ChatMemberAvatars';

interface ChatShellProps {
  partnerName: string;
  subtitle?: string | null;
  titleBadge?: React.ReactNode;
  isTeamChat?: boolean;
  isSupportChat?: boolean;
  members?: readonly ChatParticipant[];
  avatarByUserId?: ChatAvatarMap;
  currentUserId?: string;
  tab: number;
  onTabChange: (value: number) => void;
  onBack: () => void;
  onRefresh?: () => void;
  refreshingTimeline?: boolean;
  refreshDisabled?: boolean;
  children: React.ReactNode;
}

const ChatShell: React.FC<ChatShellProps> = ({
  partnerName,
  subtitle,
  titleBadge,
  isTeamChat = false,
  isSupportChat = false,
  members = [],
  avatarByUserId,
  currentUserId,
  tab,
  onTabChange,
  onBack,
  onRefresh,
  refreshingTimeline = false,
  refreshDisabled = false,
  children,
}) => {
  const { t } = useTranslation();
  const showRefresh = tab === 0 && Boolean(onRefresh);

  return (
    <Box sx={chatPageSx}>
      <Box
        sx={{
          px: { xs: 1.5, sm: 2.5 },
          pt: 1,
          pb: 0,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'rgba(0,0,0,0.2)',
          flexShrink: 0,
        }}
      >
        <Stack direction="row" alignItems="flex-start" spacing={0.5} sx={{ mb: 0.5 }}>
          <IconButton size="small" aria-label={t('common.back', 'Back')} onClick={onBack} sx={{ mt: 0.15 }}>
            <ArrowBackRoundedIcon fontSize="small" />
          </IconButton>
          {isTeamChat && avatarByUserId ? (
            <Box sx={{ pt: 0.25, flexShrink: 0 }}>
              <ChatMemberAvatars members={members} avatarByUserId={avatarByUserId} max={3} size={32} />
            </Box>
          ) : isSupportChat ? (
            <SupportAgentRoundedIcon sx={{ fontSize: 18, color: 'text.secondary', opacity: 0.9, mt: 0.6 }} />
          ) : isTeamChat ? (
            <GroupsRoundedIcon sx={{ fontSize: 18, color: 'text.secondary', opacity: 0.9, mt: 0.6 }} />
          ) : (
            <TagRoundedIcon sx={{ fontSize: 18, color: 'text.secondary', opacity: 0.9, mt: 0.6 }} />
          )}
          <Box sx={{ flex: 1, minWidth: 0, pt: 0.15 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
              <Typography
                component="h1"
                noWrap
                sx={{
                  fontWeight: 700,
                  fontSize: '1.05rem',
                  letterSpacing: '-0.01em',
                  lineHeight: 1.3,
                  minWidth: 0,
                }}
              >
                {partnerName}
              </Typography>
              {titleBadge}
            </Stack>
            {subtitle && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.15 }}>
                {subtitle}
              </Typography>
            )}
            {isTeamChat && members.length > 0 && avatarByUserId && (
              <ChatMemberChips
                members={members}
                avatarByUserId={avatarByUserId}
                currentUserId={currentUserId}
              />
            )}
          </Box>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Tabs
            value={tab}
            onChange={(_, value: number) => onTabChange(value)}
            sx={{
              flex: 1,
              minHeight: 36,
              minWidth: 0,
              '& .MuiTabs-indicator': { height: 2 },
              '& .MuiTab-root': {
                minHeight: 36,
                py: 0.75,
                px: 1.5,
                textTransform: 'none',
                fontSize: '0.875rem',
                fontWeight: 500,
                gap: 0.75,
              },
              '& .MuiTab-icon': {
                margin: 0,
                fontSize: '1.125rem',
              },
            }}
          >
            <Tab
              icon={<ForumRoundedIcon />}
              iconPosition="start"
              label={t('chat.tabs.timeline', 'Timeline')}
            />
            <Tab
              icon={<AttachFileRoundedIcon />}
              iconPosition="start"
              label={t('chat.tabs.files', 'Files')}
            />
            <Tab
              icon={<BookmarkRoundedIcon />}
              iconPosition="start"
              label={t('chat.tabs.library', 'Library')}
            />
          </Tabs>
          {showRefresh && (
            <IconButton
              size="small"
              onClick={onRefresh}
              disabled={refreshingTimeline || refreshDisabled}
              aria-label={t('chat.timeline.refresh', 'Refresh messages')}
              sx={{
                flexShrink: 0,
                color: 'text.secondary',
                width: 32,
                height: 32,
                mb: 0.25,
              }}
            >
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {refreshingTimeline ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <RefreshRoundedIcon sx={{ fontSize: 20 }} />
                )}
              </Box>
            </IconButton>
          )}
        </Stack>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>{children}</Box>
    </Box>
  );
};

export default ChatShell;
